import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api, formatApiError, fmtRub } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, TrendUp, TrendDown, Trash, CaretLeft, CaretRight } from "@phosphor-icons/react";
import BudgetCharts from "@/components/budget/BudgetCharts";
import BudgetLimits from "@/components/budget/BudgetLimits";

const expenseCategories = ["Продукты", "Транспорт", "Жильё", "Развлечения", "Здоровье", "Одежда", "Кафе", "Другое"];
const incomeCategories = ["Зарплата", "Фриланс", "Инвестиции", "Подарки", "Другое"];

export default function Budget() {
  const [month, setMonth] = useState(dayjs().format("YYYY-MM"));
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [trend, setTrend] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "expense", amount: "", category: "Продукты", note: "", date: dayjs().format("YYYY-MM-DD") });

  const load = () => {
    api.get(`/budget/summary?month=${month}`).then((r) => setSummary(r.data)).catch(() => {});
    api.get(`/budget/transactions?month=${month}`).then((r) => setTxs(r.data)).catch(() => {});
    api.get("/budget/trend?months=6").then((r) => setTrend(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [month]); // eslint-disable-line

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/budget/transactions", { ...form, amount: parseFloat(form.amount) });
      setForm((f) => ({ ...f, amount: "", note: "" }));
      setOpen(false);
      toast.success("Операция добавлена");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    await api.delete(`/budget/transactions/${id}`);
    load();
  };

  const cats = form.type === "expense" ? expenseCategories : incomeCategories;
  const maxCat = summary?.by_category?.[0]?.amount || 1;

  return (
    <div className="fade-in" data-testid="budget-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Финансы</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Бюджет</h1>
        </div>
        <div className="flex items-center gap-1 sm:gap-3 flex-wrap">
          <button onClick={() => setMonth(dayjs(month).subtract(1, "month").format("YYYY-MM"))} data-testid="budget-prev-month" className="p-2 rounded-md hover:bg-secondary transition-colors"><CaretLeft size={18} /></button>
          <span className="font-heading font-semibold capitalize w-32 text-center num" data-testid="budget-month-label">{dayjs(month).format("MMMM YYYY")}</span>
          <button onClick={() => setMonth(dayjs(month).add(1, "month").format("YYYY-MM"))} data-testid="budget-next-month" className="p-2 rounded-md hover:bg-secondary transition-colors"><CaretRight size={18} /></button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-transaction-button" className="active:scale-95 transition-transform">
                <Plus size={16} className="mr-1" /> Операция
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-modal">
              <DialogHeader><DialogTitle>Новая операция</DialogTitle></DialogHeader>
              <form onSubmit={add} className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" data-testid="tx-type-expense" onClick={() => setForm((f) => ({ ...f, type: "expense", category: "Продукты" }))}
                    className={`py-2 rounded-md text-sm font-medium transition-colors ${form.type === "expense" ? "bg-[#ff3b30]/20 text-[#ff3b30]" : "bg-secondary text-muted-foreground"}`}>
                    Расход
                  </button>
                  <button type="button" data-testid="tx-type-income" onClick={() => setForm((f) => ({ ...f, type: "income", category: "Зарплата" }))}
                    className={`py-2 rounded-md text-sm font-medium transition-colors ${form.type === "income" ? "bg-[#00e676]/20 text-[#00e676]" : "bg-secondary text-muted-foreground"}`}>
                    Доход
                  </button>
                </div>
                <div className="space-y-1.5">
                  <Label>Сумма (₽)</Label>
                  <Input data-testid="tx-amount-input" type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="1500" required />
                </div>
                <div className="space-y-1.5">
                  <Label>Категория</Label>
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="tx-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="glass-modal">
                      {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Дата</Label>
                  <Input data-testid="tx-date-input" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} required />
                </div>
                <div className="space-y-1.5">
                  <Label>Заметка (необязательно)</Label>
                  <Input data-testid="tx-note-input" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Комментарий" />
                </div>
                <Button type="submit" className="w-full" data-testid="tx-submit-button">Добавить</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 mb-6 stagger">
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-2">
            <TrendUp size={16} className="text-[#00e676]" /> Доходы
          </div>
          <div className="font-heading text-3xl font-bold num text-[#00e676]" data-testid="budget-income">{fmtRub(summary?.income)}</div>
        </div>
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-2">
            <TrendDown size={16} className="text-[#ff3b30]" /> Расходы
          </div>
          <div className="font-heading text-3xl font-bold num text-[#ff3b30]" data-testid="budget-expense">{fmtRub(summary?.expense)}</div>
        </div>
        <div className="glass-card p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Баланс</div>
          <div className="font-heading text-3xl font-bold num" data-testid="budget-balance">{fmtRub(summary?.balance)}</div>
        </div>
      </div>

      <BudgetCharts txs={txs} byCategory={summary?.by_category || []} trend={trend} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 glass-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border/40 text-xs uppercase tracking-widest text-muted-foreground">
            Операции · {txs.length}
          </div>
          {txs.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground" data-testid="budget-empty">Нет операций за этот месяц</p>
          ) : (
            <div className="max-h-[480px] overflow-y-auto">
              {txs.map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-4 py-3 border-b border-border/40 last:border-b-0 hover:bg-secondary/40 transition-colors" data-testid={`tx-row-${t.id}`}>
                  <span className={`w-1.5 h-8 rounded-full shrink-0 ${t.type === "income" ? "bg-[#00e676]" : "bg-[#ff3b30]"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{t.category}</div>
                    {t.note && <div className="text-xs text-muted-foreground truncate">{t.note}</div>}
                  </div>
                  <span className="text-xs text-muted-foreground num">{t.date}</span>
                  <span className={`num text-sm font-semibold ${t.type === "income" ? "text-[#00e676]" : "text-[#ff3b30]"}`}>
                    {t.type === "income" ? "+" : "−"}{fmtRub(t.amount)}
                  </span>
                  <button onClick={() => remove(t.id)} data-testid={`tx-delete-${t.id}`} className="text-muted-foreground hover:text-destructive transition-colors"><Trash size={15} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-4">
          <BudgetLimits month={month} refreshKey={txs.length} />
          <div className="glass-card p-6">
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Расходы по категориям</h2>
          {(summary?.by_category || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Нет данных</p>
          ) : (
            <div className="space-y-3">
              {summary.by_category.map((c) => (
                <div key={c.category}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{c.category}</span>
                    <span className="num text-muted-foreground">{fmtRub(c.amount)}</span>
                  </div>
                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${(c.amount / maxCat) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
